import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import AssignMoreTestersDialog from "@/components/AssignMoreTestersDialog";
import { ArrowLeft, Users, Bug, RefreshCw, AlertTriangle, CheckCircle2, XCircle, ExternalLink, Eye, FileText, Plus } from "lucide-react";
import { toast } from "sonner";

const scoreColor = (n) => (n >= 80 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : n >= 60 ? "text-amber-700 bg-amber-50 border-amber-200"
  : "text-red-700 bg-red-50 border-red-200");

const SEV_COLOR = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function TaskGroup() {
  const { gid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAssign, setOpenAssign] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/task-groups/${gid}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load task group");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [gid]);

  const decide = async (task_id, action) => {
    try {
      await api.post(`/tasks/${task_id}/review`, { action });
      toast.success(`Submission ${action === "accept" ? "approved" : "rejected"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  if (loading || !data) {
    return (
      <AppLayout title="Task group" subtitle="Loading…">
        <div className="text-sm text-slate-500">Loading task group…</div>
      </AppLayout>
    );
  }

  const g = data.group;
  const issues = data.issues || [];
  const submissions = data.submissions || [];
  const sevOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  const sortedIssues = [...issues].sort((a, b) => (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9));

  return (
    <AppLayout title="Task group" subtitle={g.title} data-testid="task-group-page">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => nav(-1)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5" data-testid="btn-group-back">
          <ArrowLeft className="w-4 h-4"/> Back
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setOpenAssign(true)} className="border-slate-300" data-testid="btn-group-assign-more">
            <Plus className="w-3.5 h-3.5 mr-1.5"/> Assign more testers
          </Button>
          {(() => {
            const reportable = (submissions || []).find(s => ["Submitted","Accepted","Approved","Paid","Rejected"].includes(s.status));
            if (!reportable) return null;
            return (
              <Button
                onClick={() => nav(`/tasks/${reportable.task_id}/report`)}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="btn-compile-group-report"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5"/> Compile task report
              </Button>
            );
          })()}
          <Button variant="outline" onClick={load} className="border-slate-300" data-testid="btn-group-reload">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5"/> Reload
          </Button>
        </div>
      </div>

      <div className="kpi-card mb-4" data-testid="task-group-header">
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <StatusBadge kind="task" value={g.task_type || "Task"}/>
          <span className="text-xs text-slate-500">Project:</span>
          <Link to={`/projects/${g.project_id}`} className="text-sm font-medium text-slate-900 hover:text-cyan-700">{g.project_name}</Link>
          <span className="text-xs text-slate-400">· {g.client_name || "Internal"}</span>
        </div>
        <h2 className="font-display text-xl font-bold text-slate-900">{g.title}</h2>
        {g.description && <p className="text-sm text-slate-600 mt-1.5 whitespace-pre-wrap">{g.description}</p>}
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <div><span className="uppercase tracking-wide">Allocated</span>: {g.allocated_hours ?? "—"}h</div>
          <div><span className="uppercase tracking-wide">Devices</span>: {g.device_requirements || "—"}</div>
          <div><span className="uppercase tracking-wide">Environment</span>: {g.test_environment || "—"}</div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-3" data-testid="task-group-submissions">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5"/> Testers on this task ({submissions.length})
          </div>
          {submissions.length === 0 && <div className="kpi-card text-sm text-slate-500">No testers assigned yet.</div>}
          {submissions.map(s => (
            <div key={s.task_id} className="kpi-card" data-testid={`group-submission-${s.task_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <Link to={`/tasks/${s.task_id}`} className="font-medium text-slate-900 hover:text-cyan-700 text-sm truncate flex items-center gap-1">
                    {s.tester.name} <ExternalLink className="w-3 h-3"/>
                  </Link>
                  <div className="text-[11px] text-slate-500">
                    {s.tester.tier ? <span className="mr-1.5 uppercase tracking-wide">{s.tester.tier}</span> : null}
                    <StatusBadge kind="task" value={s.status}/>
                    <span className="ml-2">{s.submitted_at ? `submitted ${new Date(s.submitted_at).toLocaleString()}` : "not yet submitted"}</span>
                  </div>
                </div>
                {s.status !== "Assigned" && s.status !== "In Progress" && (
                  <div className={`text-center border rounded-md px-3 py-1 shrink-0 ${scoreColor(s.submission_score)}`} data-testid={`group-score-${s.task_id}`}>
                    <div className="text-[10px] uppercase">Score</div>
                    <div className="font-mono text-base leading-tight">{s.submission_score}</div>
                  </div>
                )}
              </div>

              {s.is_low && s.insights.length > 0 && (
                <div className="mt-2 border border-red-200 bg-red-50 rounded-md p-2 flex gap-2" data-testid={`group-insights-${s.task_id}`}>
                  <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5"/>
                  <ul className="text-[11px] text-red-900 list-disc pl-4 space-y-0.5">
                    {s.insights.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              )}

              <div className="mt-2 grid md:grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Submission notes</div>
                  <div className="text-slate-800 whitespace-pre-wrap line-clamp-3">{s.submission_notes || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500">Issues ({s.issues_stats.total})</div>
                  <div className="text-slate-600">Valid {s.issues_stats.valid} · Duplicate {s.issues_stats.duplicate} · Rejected {s.issues_stats.rejected} · New {s.issues_stats.new}</div>
                </div>
              </div>

              {s.status === "Submitted" && (
                <div className="mt-3 pt-2 border-t border-slate-100 flex gap-2">
                  <Button size="sm" onClick={() => decide(s.task_id, "accept")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`group-accept-${s.task_id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Approve</Button>
                  <Button size="sm" onClick={() => decide(s.task_id, "reject")} className="bg-red-600 hover:bg-red-700" data-testid={`group-reject-${s.task_id}`}><XCircle className="w-3.5 h-3.5 mr-1"/>Reject</Button>
                </div>
              )}
              {s.status !== "Submitted" && s.status !== "Assigned" && s.status !== "In Progress" && (
                <div className="mt-2 text-[11px] text-slate-500">
                  {s.status}{s.quality_score != null ? ` · quality ${s.quality_score}/100` : ""}
                  {s.review_notes ? <div className="italic mt-0.5">"{s.review_notes}"</div> : null}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="lg:col-span-1 space-y-3" data-testid="task-group-issues-panel">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <Bug className="w-3.5 h-3.5"/> All issues from every tester ({sortedIssues.length})
          </div>
          <div className="kpi-card">
            {sortedIssues.length === 0 && <div className="text-xs text-slate-500">No issues logged on this task yet.</div>}
            <div className="overflow-x-auto">
              {sortedIssues.length > 0 && (
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                    <tr>
                      <th className="text-left p-1.5">ID</th>
                      <th className="text-left p-1.5">Sev</th>
                      <th className="text-left p-1.5">Title</th>
                      <th className="text-left p-1.5">Reporter</th>
                      <th className="text-left p-1.5">Status</th>
                      <th className="text-right p-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedIssues.map(i => (
                      <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`group-issue-${i.id}`}>
                        <td className="p-1.5 font-mono text-slate-500">BUG-{i.id.slice(0,6).toUpperCase()}</td>
                        <td className="p-1.5"><span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] uppercase ${SEV_COLOR[i.severity] || SEV_COLOR.low}`}>{i.severity || "—"}</span></td>
                        <td className="p-1.5"><Link to={`/issues/${i.id}`} className="text-slate-900 hover:text-cyan-700 font-medium">{i.title}</Link></td>
                        <td className="p-1.5 text-slate-600">{i.reporter_name}</td>
                        <td className="p-1.5 text-slate-600">{i.status || "—"}</td>
                        <td className="p-1.5 text-right"><Link to={`/issues/${i.id}`} className="text-cyan-700 hover:text-cyan-900 inline-flex" data-testid={`group-issue-view-${i.id}`}><Eye className="w-3.5 h-3.5"/></Link></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      <AssignMoreTestersDialog
        open={openAssign}
        onOpenChange={setOpenAssign}
        taskId={submissions[0]?.task_id || ""}
        existingAssignees={submissions.map(s => s.tester?.id).filter(Boolean)}
        onCloned={load}
      />
    </AppLayout>
  );
}
